import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Loader2, Users, MousePointerClick, TrendingUp, Package, Sprout, ArrowLeft, Share2, CheckCircle2, XCircle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-card p-5 card-hover">
      <div className="flex items-center justify-between">
        <p className="mono text-xs tracking-mono uppercase text-muted-foreground">{label}</p>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <p className="font-display font-extrabold text-3xl mt-3">{value}</p>
      {sub != null && <p className="text-sm text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user || !user.is_admin) return;
    api.get("/admin/stats")
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Erreur de chargement"));
  }, [user]);

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/connexion" replace />;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div data-testid="admin-forbidden" className="max-w-md text-center">
          <p className="mono text-xs tracking-mono uppercase text-muted-foreground">403</p>
          <h1 className="font-display font-extrabold text-3xl mt-2">Accès refusé</h1>
          <p className="text-muted-foreground mt-2">Ce tableau est réservé aux administrateurs.</p>
          <Link to="/app" className="inline-flex mt-6 items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">
            <ArrowLeft className="h-4 w-4" /> Retour au dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!data && !err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-destructive">{err}</p>
      </div>
    );
  }

  const t = data.totals;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <nav className="sticky top-0 z-40 glass border-b border-border/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sprout className="h-5 w-5 text-primary" />
            <span className="font-display font-extrabold tracking-tight">FoodFend <span className="text-muted-foreground font-normal">· Admin</span></span>
          </div>
          <Link to="/app" data-testid="admin-back-btn" className="text-sm font-semibold px-4 py-2 rounded-full hover:bg-secondary transition-colors inline-flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Link>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <header className="mb-8">
          <p className="mono text-xs tracking-mono uppercase text-primary">Vue globale · admin</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-2 tracking-tight">Statistiques du site</h1>
          <p className="text-muted-foreground mt-2">Clics, inscriptions et impact — tout FoodFend en un coup d'œil.</p>
        </header>

        {/* KPIs */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testid="stat-visits" icon={MousePointerClick} label="Clics totaux" value={t.visits.toLocaleString("fr-FR")} sub={`${t.visits_7d} sur 7 j · ${t.visits_30d} sur 30 j`} />
          <StatCard testid="stat-users" icon={Users} label="Inscrits" value={t.users.toLocaleString("fr-FR")} sub={`${t.signups_7d} sur 7 j · ${t.signups_30d} sur 30 j`} />
          <StatCard testid="stat-conversion" icon={TrendingUp} label="Conversion 30 j" value={`${t.conversion_rate}%`} sub="visites → inscriptions" />
          <StatCard testid="stat-refclicks" icon={Share2} label="Clics parrainage" value={t.ref_clicks.toLocaleString("fr-FR")} sub="via un lien filleul" />
        </section>

        {/* Chart */}
        <section className="mt-8 rounded-2xl border border-border bg-card p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl">Activité — 14 derniers jours</h2>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary" /> Clics</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: "hsl(265 80% 58%)" }} /> Inscriptions</span>
            </div>
          </div>
          <div className="h-72 w-full" data-testid="admin-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.timeseries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="hsl(214 24% 90%)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tickFormatter={(d) => d.slice(5)} stroke="hsl(215 16% 47%)" fontSize={12} />
                <YAxis allowDecimals={false} stroke="hsl(215 16% 47%)" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(214 24% 90%)", background: "#fff" }} />
                <Line type="monotone" dataKey="visits" name="Clics" stroke="hsl(152 76% 40%)" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="signups" name="Inscriptions" stroke="hsl(265 80% 58%)" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Secondary blocks */}
        <section className="mt-8 grid lg:grid-cols-2 gap-6">
          {/* Products / impact */}
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display font-bold text-xl mb-4 inline-flex items-center gap-2"><Package className="h-5 w-5 text-primary" /> Impact global</h2>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-secondary p-4">
                <p className="mono text-[10px] tracking-mono uppercase text-muted-foreground">Produits</p>
                <p className="font-display font-extrabold text-2xl mt-1">{t.products}</p>
              </div>
              <div className="rounded-xl bg-secondary p-4">
                <p className="mono text-[10px] tracking-mono uppercase text-muted-foreground inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-primary" /> Sauvés</p>
                <p className="font-display font-extrabold text-2xl mt-1 text-primary">{t.saved}</p>
              </div>
              <div className="rounded-xl bg-secondary p-4">
                <p className="mono text-[10px] tracking-mono uppercase text-muted-foreground inline-flex items-center gap-1"><XCircle className="h-3 w-3 text-destructive" /> Gaspillés</p>
                <p className="font-display font-extrabold text-2xl mt-1 text-destructive">{t.wasted}</p>
              </div>
            </div>
          </div>

          {/* Top referrers */}
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display font-bold text-xl mb-4 inline-flex items-center gap-2"><Share2 className="h-5 w-5 text-primary" /> Top parrains</h2>
            {data.top_referrers.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucun clic de parrainage pour le moment.</p>
            ) : (
              <ul className="space-y-2" data-testid="top-referrers-list">
                {data.top_referrers.map((r) => (
                  <li key={r.email} className="flex items-center justify-between rounded-xl bg-secondary px-4 py-3">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{r.name || r.email}</p>
                      <p className="mono text-[10px] tracking-mono uppercase text-muted-foreground">{r.ref_code} · {r.referrals} inscrits</p>
                    </div>
                    <span className="font-display font-extrabold text-primary">{r.clicks} clics</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Recent signups */}
        <section className="mt-8 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display font-bold text-xl mb-4 inline-flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Inscriptions récentes</h2>
          {data.recent_signups.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun compte pour le moment.</p>
          ) : (
            <div className="overflow-x-auto" data-testid="recent-signups-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Nom</th>
                    <th className="py-2 pr-4 font-medium">Email</th>
                    <th className="py-2 pr-4 font-medium">Date</th>
                    <th className="py-2 pr-4 font-medium text-right">Filleuls</th>
                    <th className="py-2 pr-4 font-medium">Parrainé</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_signups.map((u) => (
                    <tr key={u.id} className="border-t border-border">
                      <td className="py-2.5 pr-4 font-medium">{u.name || "—"}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{u.email}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{(u.created_at || "").slice(0, 10)}</td>
                      <td className="py-2.5 pr-4 text-right font-semibold">{u.referrals}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{u.referred_by ? "oui" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
